"use client";

import { useState, useEffect, useCallback } from "react";
import { PortableText } from "next-sanity";
import Image from "@/helpers/Image";
import stars from "@/public/ecommerce/stars.svg";
import { CTAButton } from "@/shadcn/ui/button";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shadcn/ui/accordion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UseEmblaCarouselType } from "embla-carousel-react";
import { PageEcommerceSchema } from "@/sanity/pages/PageEcommerceTemplate";
import ScrollingPartnersLogos from "../global/ScrollingPartnersLogos";

type CarouselApi = UseEmblaCarouselType[1];

function BulletCheck() {
  return (
    <svg
      className="w-[1.5rem] h-[1rem] shrink-0 mt-[0.375rem]"
      viewBox="0 0 12 8"
      fill="none"
    >
      <path
        d="M1 4L4.5 7.5L11 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const TRUST_ICONS = [
  <svg
    key="0"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className="w-[3rem] h-[3rem] shrink-0"
  >
    <path
      d="M7 12L12 17L22 7M2 12L7 17M12 12L17 7"
      stroke="#003828"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>,
  <svg
    key="1"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className="w-[3rem] h-[3rem] shrink-0"
  >
    <path
      d="M4 13C4.325 15.532 5.881 17.781 8 19M20 11C19.7554 9.24023 18.9391 7.60967 17.6766 6.35951C16.4142 5.10934 14.7758 4.30893 13.0137 4.08156C11.2516 3.8542 9.46362 4.21249 7.9252 5.10126C6.38678 5.99002 5.18325 7.35995 4.5 9.00001M4 5V9H8M12 15H14C14.2652 15 14.5196 15.1054 14.7071 15.2929C14.8946 15.4804 15 15.7348 15 16V17C15 17.2652 14.8946 17.5196 14.7071 17.7071C14.5196 17.8946 14.2652 18 14 18H13C12.7348 18 12.4804 18.1054 12.2929 18.2929C12.1054 18.4804 12 18.7348 12 19V20C12 20.2652 12.1054 20.5196 12.2929 20.7071C12.4804 20.8946 12.7348 21 13 21H15M18 15V17C18 17.2652 18.1054 17.5196 18.2929 17.7071C18.4804 17.8946 18.7348 18 19 18H20M21 15V21"
      stroke="#003828"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>,
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    className="w-[3rem] h-[3rem] shrink-0"
  >
    <path
      d="M5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 10.8181 3.23279 9.64778 3.68508 8.55585C4.13738 7.46392 4.80031 6.47177 5.63604 5.63604C6.47177 4.80031 7.46392 4.13738 8.55585 3.68508C9.64778 3.23279 10.8181 3 12 3C13.1819 3 14.3522 3.23279 15.4442 3.68508C16.5361 4.13738 17.5282 4.80031 18.364 5.63604M5.63604 18.364C6.47177 19.1997 7.46392 19.8626 8.55585 20.3149C9.64778 20.7672 10.8181 21 12 21C13.1819 21 14.3522 20.7672 15.4442 20.3149C16.5361 19.8626 17.5282 19.1997 18.364 18.364C19.1997 17.5282 19.8626 16.5361 20.3149 15.4442C20.7672 14.3522 21 13.1819 21 12C21 10.8181 20.7672 9.64778 20.3149 8.55585C19.8626 7.46392 19.1997 6.47177 18.364 5.63604M5.63604 18.364L18.364 5.63604"
      stroke="#003828"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>,
];

export default function HeroSection({ data }: { data: PageEcommerceSchema }) {
  const images = data.heroImages ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [mainApi, setMainApi] = useState<CarouselApi>();
  const [thumbApi, setThumbApi] = useState<CarouselApi>();

  useEffect(() => {
    if (!mainApi) return;
    const handler = () => {
      const index = mainApi.selectedScrollSnap();
      setActiveIndex(index);
      thumbApi?.scrollTo(index);
    };
    mainApi.on("select", handler);
    return () => {
      mainApi.off("select", handler);
    };
  }, [mainApi, thumbApi]);

  const handleThumbClick = useCallback(
    (i: number) => {
      setActiveIndex(i);
      mainApi?.scrollTo(i);
      thumbApi?.scrollTo(i);
    },
    [mainApi, thumbApi],
  );

  if (!data.heroTitle && !images.length) return null;

  return (
    <section className="w-full bg-white pt-[4rem] lg:pt-[5rem] pb-[8rem] lg:pb-[12rem]">
      <div className="mx-auto w-[min(150rem,100%)] px-[3rem] lg:px-[5rem] flex flex-col lg:flex-row gap-[6rem] lg:gap-[9rem] items-start">
        {/* ── LEFT COLUMN ── */}
        <div className="w-full lg:w-[68.5rem] shrink-0 flex flex-col gap-[2.5rem] lg:sticky lg:top-[15rem]">
          {/* Title + description */}
          <div className="lg:hidden flex flex-col gap-[2rem]">
            {data.heroTitle && (
              <div className="richtext richtext-start prose-p:text-[1.75rem] prose-p:mt-[2rem] prose-headings:font-semibold prose-headings:text-[5.375rem] prose-headings:leading-[1.15] prose-headings:tracking-[-0.3225rem] prose-headings:mb-0 prose-strong:text-[#003828] prose-strong:font-bold prose-strong:not-italic">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
          {/* Main image carousel */}
          <Carousel
            setApi={setMainApi}
            opts={{ align: "start", loop: true }}
            className="w-full"
          >
            <CarouselContent>
              {images.map((img, i) => (
                <CarouselItem key={i} className="pl-0 px-[0.5rem] basis-full">
                  <div className="relative w-full aspect-[548/592] rounded-[1rem] overflow-hidden bg-[#d9d9d9]">
                    <Image
                      src={img}
                      alt=""
                      className="!absolute !inset-0 !w-full !h-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {images.length > 1 && (
              <>
                <button
                  onClick={() => mainApi?.scrollPrev()}
                  aria-label="Previous image"
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-tr-[1.875rem] rounded-br-[1.875rem] flex items-center justify-center px-[1.25rem] py-[1rem] transition-opacity hover:opacity-70"
                >
                  <ChevronLeft
                    className="w-[2.25rem] h-[2.25rem] text-black"
                    strokeWidth={1.25}
                  />
                </button>
                <button
                  onClick={() => mainApi?.scrollNext()}
                  aria-label="Next image"
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-tl-[1.875rem] rounded-bl-[1.875rem] flex items-center justify-center px-[1.25rem] py-[1rem] transition-opacity hover:opacity-70"
                >
                  <ChevronRight
                    className="w-[2.25rem] h-[2.25rem] text-black"
                    strokeWidth={1.25}
                  />
                </button>
              </>
            )}
          </Carousel>

          {/* Thumbnail carousel */}
          {images.length > 1 && (
            <Carousel
              setApi={setThumbApi}
              opts={{ align: "start", dragFree: true, loop: true }}
              className="w-full"
            >
              <CarouselContent>
                {images.map((img, i) => (
                  <CarouselItem key={i} className="basis-[14.25rem]">
                    <button
                      onClick={() => handleThumbClick(i)}
                      className={cn(
                        "relative w-full h-[12.25rem] rounded-[1rem] overflow-hidden bg-[#f1f1f1] border transition-all duration-200",
                        i === activeIndex
                          ? "border-[#1aa87f] ring-2 ring-[#1aa87f]/30"
                          : "border-black/10 hover:border-black/30",
                      )}
                    >
                      <Image
                        src={img}
                        alt=""
                        className="!absolute !inset-0 !w-full !h-full object-cover"
                      />
                    </button>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          )}
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="flex-1 w-full min-w-0 flex flex-col gap-[4rem]">
          {/* Rating row */}
          {(data.headerRating ||
            data.headerRatingCount ||
            data.headerPayoutText) && (
            <div className="hidden lg:flex flex-wrap items-center gap-x-[2.625rem] gap-y-[1rem]">
              {(data.headerRating || data.headerRatingCount) && (
                <div className="flex items-center gap-[1.375rem]">
                  {data.headerRating && (
                    <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] tracking-[-0.06rem] text-black">
                      {data.headerRating}
                    </p>
                  )}
                  <Image src={stars} alt="stars" className="!w-[10.5rem]" />
                  {data.headerRatingCount && (
                    <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.33] tracking-[-0.06rem] uppercase text-black/50 whitespace-nowrap">
                      <strong className="">({data.headerRatingCount})</strong>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Title + description */}
          <div className="hidden lg:flex flex-col gap-[2rem]">
            {data.heroTitle && (
              <div className="richtext richtext-start prose-p:text-[1.75rem] prose-p:mt-[2rem] prose-headings:font-semibold prose-headings:text-[5.375rem] prose-headings:leading-[1.15] prose-headings:tracking-[-0.3225rem] prose-headings:mb-0 prose-strong:text-[#003828] prose-strong:font-bold prose-strong:not-italic">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {/* Pricing card */}
          <div className="border border-[#1aa87f] rounded-[0.25rem] overflow-hidden">
            {/* "Bestes Angebot" banner */}
            {data.heroBestOfferLabel && (
              <div className="w-full h-[3.625rem] bg-[#1aa87f] flex items-center justify-center">
                <p className="mb-0 font-geist font-semibold text-[1.75rem] leading-[1.2] tracking-[-0.07rem] text-white">
                  {data.heroBestOfferLabel}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-[3rem]">
              {/* Price display */}
              {(data.headerOriginalPrice ||
                data.headerPrice ||
                data.headerDiscountLabel) && (
                <div className="flex flex-col gap-[0.875rem] px-[4.625rem] pt-[5rem] ">
                  <div className="flex items-center gap-[1.25rem]">
                    <div className="flex items-center gap-[0.875rem] text-black leading-[1.2]">
                      {data.headerOriginalPrice && (
                        <span className="line-through opacity-30 font-normal text-[4.625rem] tracking-[-0.37rem]">
                          {data.headerOriginalPrice}
                        </span>
                      )}
                      {data.headerPrice && (
                        <span className="font-semibold text-[6.375rem] tracking-[-0.6375rem]">
                          {data.headerPrice}
                        </span>
                      )}
                    </div>
                    {data.headerDiscountLabel && (
                      <div className="relative rounded-full px-[1rem] py-[0.5rem] overflow-hidden shadow-[inset_0px_4px_8px_rgba(255,255,255,0.37)]">
                        <div
                          aria-hidden
                          className="absolute inset-0 bg-gradient-to-r from-[#e49400] to-[#b36b00] pointer-events-none"
                        />
                        <span className="relative font-geist font-semibold text-[2rem] leading-[1.2] tracking-[-0.04rem] text-white whitespace-nowrap">
                          {data.headerDiscountLabel}
                        </span>
                      </div>
                    )}
                  </div>
                  {data.heroPriceSubtext && (
                    <div className="richtext richtext-start prose-p:text-[1.75rem] prose-p:leading-[1.5] prose-p:text-black/70 prose-p:mb-0 prose-strong:font-semibold prose-strong:text-black prose-strong:not-italic">
                      <PortableText
                        value={data.heroPriceSubtext}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* CTA */}
              {data.heroCTA && (
                <div className="px-[4.625rem]">
                  <CTAButton
                    data={data.heroCTA}
                    className="w-full justify-center"
                  />
                </div>
              )}

              {/* Social proof */}
              {(data.heroSocialProofImage || data.heroSocialProofText) && (
                <div className="flex items-center gap-[2rem] px-[4.625rem]">
                  {data.heroSocialProofImage && (
                    <div className="relative w-[6.75rem] shrink-0">
                      <Image
                        src={data.heroSocialProofImage}
                        alt=""
                        className="!w-full !h-full object-cover"
                      />
                    </div>
                  )}
                  {data.heroSocialProofText && (
                    <div className="richtext richtext-start prose-p:text-[1.75rem] prose-p:leading-[1.5] prose-p:text-black/70 prose-p:mb-0 prose-strong:font-semibold prose-strong:text-black prose-strong:not-italic">
                      <PortableText
                        value={data.heroSocialProofText}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Benefits checklist */}
              {!!data.heroBenefits?.length && (
                <div className="flex flex-col gap-[2rem] bg-[#F4F4F4] p-[4rem]">
                  {data.heroBenefits.map((b, i) => (
                    <div
                      key={b._key ?? i}
                      className="flex items-center gap-[1.375rem] text-black/60"
                    >
                      <BulletCheck />
                      <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.5]">
                        {b.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Trust badges */}
          {!!data.heroTrustBadges?.length && (
            <div className="flex gap-x-[4rem] gap-y-[2rem]">
              {data.heroTrustBadges.map((badge, i) => {
                return (
                  <div
                    key={badge._key ?? i}
                    className="flex items-center gap-[0.875rem]"
                  >
                    {TRUST_ICONS[i % TRUST_ICONS.length]}
                    <div className="flex flex-col">
                      <p className="mb-0 font-geist font-medium text-[1.75rem] leading-[1.5] text-black/62">
                        {badge.label}
                      </p>
                      {badge.sublabel && (
                        <p className="mb-0 font-geist text-[1.5rem] leading-[1.33] text-black/40">
                          {badge.sublabel}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Divider */}
          <div className="h-px bg-black/10 w-full" />

          {/* Endorsements */}
          {(data.heroEndorsementsTitle ||
            !!data.heroEndorserLogos?.length ||
            !!data.heroEndorserCards?.length) && (
            <div className="flex flex-col gap-[1rem]">
              {(data.heroEndorsementsTitle ||
                data.heroEndorsementsDescription) && (
                <div className="flex flex-col gap-[1.25rem]">
                  {data.heroEndorsementsTitle && (
                    <p className="mb-0 font-geist font-medium text-[2.625rem] leading-[1.15] tracking-[-0.18rem] text-black">
                      {data.heroEndorsementsTitle}
                    </p>
                  )}
                  {data.heroEndorsementsDescription && (
                    <p className="mb-0 font-geist font-normal text-[1.75rem] leading-[1.5] text-black/70">
                      {data.heroEndorsementsDescription}
                    </p>
                  )}
                </div>
              )}

              {/* Logo strip */}
              {!!data.heroEndorserLogos?.length && (
                <ScrollingPartnersLogos
                  logos={data.heroEndorserLogos}
                  theme="dark-tinted"
                  fadeColor="transparent"
                  logoHeightRem={3}
                />
              )}

              {/* Portrait cards */}
              {!!data.heroEndorserCards?.length && (
                <Carousel
                  opts={{ align: "start", dragFree: true }}
                  className="w-full"
                >
                  <CarouselContent className="-ml-[1rem]">
                    {data.heroEndorserCards.map((card, i) => (
                      <CarouselItem
                        key={card._key ?? i}
                        className="pl-[1rem] basis-[25.875rem]"
                      >
                        <div className="border border-black/10 bg-white rounded-[0.25rem] overflow-hidden">
                          <div className="w-full h-[22rem] overflow-hidden">
                            {card.image && (
                              <Image
                                src={card.image}
                                alt={card.name ?? ""}
                                className="!w-full !h-full object-cover object-top"
                              />
                            )}
                          </div>
                          <div className="px-[2.5rem] py-[2rem] flex flex-col gap-[0.375rem]">
                            {card.name && (
                              <p className="mb-0 font-blacker italic text-[2.25rem] leading-[1.12] tracking-[-0.0675rem] text-black">
                                {card.name}
                              </p>
                            )}
                            {card.role && (
                              <p className="mb-0 font-geist font-medium text-[1.5rem] leading-[1.33] text-black/62">
                                {card.role}
                              </p>
                            )}
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              )}
            </div>
          )}

          {/* Divider */}
          {!!data.heroFaqs?.length && (
            <div className="h-px bg-black/10 w-full" />
          )}

          {/* FAQ title */}
          {data.heroFaqTitle && (
            <p className="mb-0 font-geist font-medium text-[2.625rem] leading-[1.15] tracking-[-0.18rem] text-black">
              {data.heroFaqTitle}
            </p>
          )}

          {/* FAQ accordion */}
          {!!data.heroFaqs?.length && (
            <Accordion
              type="single"
              collapsible
              defaultValue={data.heroFaqs[0]?._key ?? "0"}
            >
              {data.heroFaqs.map((faq, i) => (
                <AccordionItem
                  key={faq._key ?? i}
                  value={faq._key ?? String(i)}
                  className={cn(
                    "border-b border-black/10 data-[state=open]:border-b-[#1AA87F]",
                    i === 0 && "border-t",
                  )}
                >
                  <AccordionTrigger className="font-geist font-medium text-[2rem] leading-[1.12] tracking-[-0.08rem] text-black py-[2rem] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-geist font-normal text-[1.75rem] leading-[1.5] text-black/50 pb-[2rem]">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}

          {/* CTA */}
          {data.heroCTA && (
            <div className="">
              <CTAButton
                data={data.heroCTA}
                className="w-full justify-center"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
